/**
 * Commands that change the shape of the piece: its header, its bars and its parts.
 *
 * These are the edits that have to keep two sides in step. A bar exists on the global
 * timeline *and* in every part; a part exists in the part list *and* in whatever groups
 * bracket it. An applier here that updates one side and forgets the other leaves a
 * document that reads fine and exports wrong, so each one does both or does nothing.
 */

import { emptyPartMeasure, globalMeasure } from "../document/create";
import type { MeasureId, PartId } from "../document/ids";
import type { GlobalMeasure, Part, PartMeasure, Score, ScoreMeta } from "../document/types";
import type { Layout } from "../document/layout";
import { done, fail, findPart } from "./helpers";
import type {
  CommandResult,
  InsertMeasure,
  InsertPart,
  RemoveMeasure,
  RemovePart,
  RestoreMeasure,
  RestorePart,
  SetGroups,
  SetLayout,
  SetMeasure,
  SetMeta,
  SetPart,
} from "./types";

/** The values `props` is about to overwrite, so that the inverse restores exactly them. */
function previous<T extends object>(source: T, props: Partial<T>): Partial<T> {
  const before: Partial<T> = {};
  for (const key of Object.keys(props) as (keyof T)[]) before[key] = source[key];
  return before;
}

export function applySetMeta(score: Score, command: SetMeta): CommandResult {
  const meta: ScoreMeta = { ...score.meta, ...command.meta };
  return done({ ...score, meta }, { kind: "setMeta", meta: previous(score.meta, command.meta) });
}

export function applySetLayout(score: Score, command: SetLayout): CommandResult {
  const layout: Layout = { ...score.layout, ...command.layout };
  return done(
    { ...score, layout },
    { kind: "setLayout", layout: previous(score.layout, command.layout) },
  );
}

export function applySetMeasure(score: Score, command: SetMeasure): CommandResult {
  const index = score.measures.findIndex((measure) => measure.id === command.id);
  const measure = score.measures[index];
  if (measure === undefined) return fail(command, `no measure ${command.id}`);

  const measures = [...score.measures];
  measures[index] = { ...measure, ...command.props, id: measure.id };
  return done(
    { ...score, measures },
    { kind: "setMeasure", id: command.id, props: previous(measure, command.props) },
  );
}

export function applyInsertMeasure(score: Score, command: InsertMeasure): CommandResult {
  const at = Math.max(0, Math.min(command.index, score.measures.length));
  const measure = globalMeasure(command.props ?? {});
  const measures = [...score.measures];
  measures.splice(at, 0, measure);

  const parts = score.parts.map((part) => ({
    ...part,
    measures: { ...part.measures, [measure.id]: emptyPartMeasure(part.staves.length) },
  }));

  return done({ ...score, measures, parts }, { kind: "removeMeasure", id: measure.id });
}

/**
 * Removing a bar takes every part's music in it away too, so the inverse has to carry
 * that music: a plain insert would put back an empty bar and call it undone.
 */
export function applyRemoveMeasure(score: Score, command: RemoveMeasure): CommandResult {
  const index = score.measures.findIndex((measure) => measure.id === command.id);
  const measure = score.measures[index];
  if (measure === undefined) return fail(command, `no measure ${command.id}`);
  if (score.measures.length <= 1) return fail(command, "a score keeps at least one measure");

  const content: Record<PartId, PartMeasure> = {};
  const parts = score.parts.map((part) => {
    const held = part.measures[command.id];
    if (held !== undefined) content[part.id] = held;
    const measures = { ...part.measures };
    delete measures[command.id];
    return { ...part, measures };
  });

  return done(
    { ...score, measures: score.measures.filter((m) => m.id !== command.id), parts },
    { kind: "restoreMeasure", index, measure, content },
  );
}

export function applyRestoreMeasure(score: Score, command: RestoreMeasure): CommandResult {
  const id: MeasureId = command.measure.id;
  if (score.measures.some((measure) => measure.id === id)) {
    return fail(command, `measure ${id} is already in the score`);
  }

  const at = Math.max(0, Math.min(command.index, score.measures.length));
  const measures: GlobalMeasure[] = [...score.measures];
  measures.splice(at, 0, command.measure);

  const parts = score.parts.map((part) => ({
    ...part,
    measures: {
      ...part.measures,
      [id]: command.content[part.id] ?? emptyPartMeasure(part.staves.length),
    },
  }));

  return done({ ...score, measures, parts }, { kind: "removeMeasure", id });
}

/** A part joining the score is given a bar for every bar the score already has. */
export function applyInsertPart(score: Score, command: InsertPart): CommandResult {
  if (findPart(score, command.part.id) !== null) {
    return fail(command, `part ${command.part.id} is already in the score`);
  }

  const measures: Record<MeasureId, PartMeasure> = { ...command.part.measures };
  for (const measure of score.measures) {
    measures[measure.id] ??= emptyPartMeasure(command.part.staves.length);
  }

  const parts: Part[] = [...score.parts];
  parts.splice(Math.max(0, Math.min(command.index, parts.length)), 0, {
    ...command.part,
    measures,
  });

  return done({ ...score, parts }, { kind: "removePart", id: command.part.id });
}

export function applyRemovePart(score: Score, command: RemovePart): CommandResult {
  const index = score.parts.findIndex((part) => part.id === command.id);
  const part = score.parts[index];
  if (part === undefined) return fail(command, `no part ${command.id}`);

  const groups = score.groups
    .map((group) => ({ ...group, parts: group.parts.filter((id) => id !== command.id) }))
    .filter((group) => group.parts.length > 0);

  return done(
    { ...score, parts: score.parts.filter((existing) => existing.id !== command.id), groups },
    { kind: "restorePart", index, part, groups: score.groups },
  );
}

export function applyRestorePart(score: Score, command: RestorePart): CommandResult {
  const inserted = applyInsertPart(score, {
    kind: "insertPart",
    index: command.index,
    part: command.part,
  });
  if (!inserted.ok) return fail(command, inserted.reason);
  return done(
    { ...inserted.score, groups: command.groups },
    { kind: "removePart", id: command.part.id },
  );
}

export function applySetPart(score: Score, command: SetPart): CommandResult {
  const part = findPart(score, command.id);
  if (part === null) return fail(command, `no part ${command.id}`);

  const { instrument, ...rest } = command.props;
  const next: Part = {
    ...part,
    ...rest,
    instrument: instrument === undefined ? part.instrument : { ...part.instrument, ...instrument },
  };

  const before: SetPart["props"] = {
    ...previous(part, rest),
    ...(instrument === undefined ? {} : { instrument: previous(part.instrument, instrument) }),
  };

  return done(
    { ...score, parts: score.parts.map((p) => (p.id === part.id ? next : p)) },
    { kind: "setPart", id: command.id, props: before },
  );
}

export function applySetGroups(score: Score, command: SetGroups): CommandResult {
  return done({ ...score, groups: command.groups }, { kind: "setGroups", groups: score.groups });
}
